import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { enqueue, utilityEvents as utilityEventsCache } from "../local/db";
import { syncEngine } from "../local/sync";

// Utility events (BLOOD / LAMENESS, card #139) — persisted on the Ride
// server-side, delivered cross-scribe by polling. Every scribe/judge terminal
// scoring a ride polls GET /ride/:rideId/utility-events on this interval
// while the sheet is open, so a flag raised at one judge position shows up at
// every other position scoring the same ride. Modeled on announcer-fe's
// useClassResults (visibility-aware poll + read-through offline cache).
export const UTILITY_EVENTS_POLL_MS = 4000;

/**
 * @param {string} rideId
 * @param {{ online?: boolean }} [opts]
 * @returns {{
 *   events: Array,
 *   error: Error|null,
 *   raise: (type: string) => Promise<void>,
 *   clear: (eventId: string) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useRideUtilityEvents(rideId, { online = true } = {}) {
  const scoreApi = useScoreApi();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const onlineRef = useRef(online);
  onlineRef.current = online;

  // Monotonic request-generation counter (review fix, card #139): refresh()
  // can be in flight (poll interval, or a raise()/clear() triggering an
  // extra refresh) when a newer refresh() is issued — e.g. an interval tick
  // that started before a raise() lands can resolve after it, and a blind
  // setEvents(rows) from that stale response would momentarily blank a
  // just-raised flag. Every refresh() call captures the generation at start
  // and discards its own result if a newer call has since been issued.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      if (onlineRef.current) {
        const rows =
          unwrap(await apiRef.current.rides.listRideUtilityEvents(id)) || [];
        if (isStale()) return;
        setEvents(rows);
        setError(null);
        await utilityEventsCache.put({
          rideId: id,
          events: rows,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const cached = await utilityEventsCache.get(id);
        if (isStale()) return;
        setEvents(cached?.events ?? []);
      }
    } catch (err) {
      if (isStale()) return;
      // Network failed after a possible cache hit — fall back to the last
      // known state rather than blanking the banner.
      const cached = await utilityEventsCache.get(id);
      if (isStale()) return;
      if (cached) {
        setEvents(cached.events);
        setError(null);
      } else {
        setError(err);
      }
    }
  }, []);

  useEffect(() => {
    if (!rideId) {
      setEvents([]);
      return undefined;
    }
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, UTILITY_EVENTS_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [rideId, refresh, online]);

  // Raise is a QUEUED write (card #139 AC: survives reload + an offline
  // period) — enqueued into the same IndexedDB outbox the score-save path
  // uses, never a direct fetch. An optimistic local entry renders
  // immediately (even offline); it's replaced by the server row once the op
  // drains and the next refresh() lands.
  const raise = useCallback(
    async (type) => {
      const id = rideIdRef.current;
      if (!id || !type) return;
      const opId = `utilityEvent:raise:${id}:${type}:${Date.now()}`;
      const optimistic = {
        id: opId,
        type,
        raisedByScribeId: null,
        raisedAt: new Date().toISOString(),
        _pending: true,
      };
      setEvents((prev) => [...prev, optimistic]);
      await enqueue({
        opId,
        type: "raiseUtilityEvent",
        entityKey: id,
        payload: { rideId: id, type },
      });
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  // Clear is likewise queued. Optimistically removed locally; the server is
  // the authoritative gate (originator-or-admin, card #139) — if a stale/
  // unauthorized clear is rejected, the next refresh() brings it back.
  const clear = useCallback(
    async (eventId) => {
      const id = rideIdRef.current;
      if (!id || !eventId) return;
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      await enqueue({
        opId: `utilityEvent:clear:${id}:${eventId}`,
        type: "clearUtilityEvent",
        entityKey: id,
        payload: { rideId: id, eventId },
      });
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  return { events, error, raise, clear, refresh };
}

export default useRideUtilityEvents;
